<template>
    <section
        class="perspective-new-chat-offer relative overflow-hidden rounded-xl border border-border bg-surface-raised"
        aria-labelledby="perspective-new-chat-offer-title"
        data-testid="perspective-new-chat-offer">
        <BaseButton
            theme="tertiary"
            size="sm"
            icon-only
            aria-label="Dismiss Perspective offer"
            class="absolute top-3 right-3 text-ink-muted"
            :disabled="isDismissing"
            data-testid="perspective-new-chat-offer-dismiss"
            @click="handleDismiss">
            <i class="bi bi-x text-xl leading-none" aria-hidden="true"></i>
        </BaseButton>
        <div class="flex flex-col gap-4 p-5 pr-14">
            <div class="flex items-center gap-4">
                <svg class="perspective-mark shrink-0 overflow-visible" width="60" height="28" viewBox="0 0 30 14" fill="none" aria-hidden="true">
                    <circle class="perspective-mark-dot fill-perspective-mark-magenta" style="--i: 0" cx="7" cy="7" r="7" />
                    <circle class="perspective-mark-dot fill-perspective-mark-amber" style="--i: 1" cx="15" cy="7" r="7" />
                    <circle class="perspective-mark-dot fill-perspective-mark-cyan" style="--i: 2" cx="23" cy="7" r="7" />
                    <defs>
                        <clipPath :id="`${markId}-a`"><circle class="perspective-mark-dot" style="--i: 0" cx="7" cy="7" r="7" /></clipPath>
                        <clipPath :id="`${markId}-b`"><circle class="perspective-mark-dot" style="--i: 1" cx="15" cy="7" r="7" /></clipPath>
                        <clipPath :id="`${markId}-c`"><circle class="perspective-mark-dot" style="--i: 2" cx="23" cy="7" r="7" /></clipPath>
                    </defs>
                    <g :clip-path="`url(#${markId}-a)`">
                        <rect
                            class="perspective-mark-overlap fill-perspective-mark-magenta-amber"
                            style="--i: 1"
                            x="-900"
                            y="-900"
                            width="1800"
                            height="1800"
                            :clip-path="`url(#${markId}-b)`" />
                    </g>
                    <g :clip-path="`url(#${markId}-b)`">
                        <rect
                            class="perspective-mark-overlap fill-perspective-mark-amber-cyan"
                            style="--i: 2"
                            x="-900"
                            y="-900"
                            width="1800"
                            height="1800"
                            :clip-path="`url(#${markId}-c)`" />
                    </g>
                </svg>
                <p class="perspective-new-chat-offer-eyebrow text-perspective-offer-eyebrow">Perspective</p>
            </div>
            <div class="flex flex-col gap-1">
                <h3 id="perspective-new-chat-offer-title" class="display-small-serif text-ink">Want coaching that’s more tailored to you?</h3>
                <p class="body text-ink-muted">
                    Complete your Perspective so I understand how you communicate, make decisions, and play to your strengths. I’ll use it to personalize my coaching from then on.
                </p>
            </div>
        </div>
        <div class="flex flex-col gap-2 border-t border-border bg-surface px-5 py-3.5 sm:flex-row sm:items-center sm:justify-end">
            <p v-if="errorMessage" role="alert" class="details text-status-error sm:mr-auto">{{ errorMessage }}</p>
            <BaseButton
                theme="primary"
                class="inline-flex min-h-11 w-full items-center justify-center sm:min-h-0 sm:w-auto"
                :disabled="isStartingAssessment"
                data-testid="perspective-new-chat-offer-cta"
                @click="handleStart">
                <span class="flex items-center gap-2">
                    <LoadingSpinner v-if="isStartingAssessment" class="size-4" />
                    <span>Take the quiz</span>
                </span>
            </BaseButton>
        </div>
    </section>
</template>

<script setup lang="ts">
import { usePage } from "@inertiajs/vue3";
import { useFetch } from "~vue/api";
import type { PerspectiveNewChatOffer } from "~vue/ChatDetail/types";
import BaseButton from "~vue/components/BaseButton.vue";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import { usePerspective } from "~vue/Perspective/composable/usePerspective";
import { openUrl } from "~vue/utils";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, onMounted, ref, useId } from "vue";

const SURFACE = "new_chat_banner" as const;
const DISMISS_INTERACTION_TYPE = "perspective_new_chat_offer_dismissed";

const props = withDefaults(
    defineProps<{
        offer: PerspectiveNewChatOffer;
        coachingSessionUuid?: string | null;
    }>(),
    { coachingSessionUuid: null },
);

const emit = defineEmits<{ dismiss: [] }>();

const markId = useId();
const page = usePage<{ align_host: string }>();
const { handleStartAssessment, isStartingAssessment, startAssessmentErrorMessage } = usePerspective({ source: SURFACE });

const dismissPayload = ref<{ interaction_type: string } | null>(null);
const { execute: persistDismiss, error: dismissError, isFetching: isDismissing } = useFetch("/api/accounts/ui-interactions", { immediate: false }).post(dismissPayload).json();

const errorMessage = computed(() => startAssessmentErrorMessage.value || (dismissError.value ? "Couldn't dismiss right now. Please try again." : null));

function logOfferEvent(eventName: AmplitudeEventNames) {
    return logUserInteraction(
        eventName,
        {
            surface: SURFACE,
            path: props.offer.path,
            offer_sequence_number: props.offer.offer_sequence_number,
            dismiss_count: props.offer.dismiss_count,
            ...(props.coachingSessionUuid ? { coaching_session_uuid: props.coachingSessionUuid } : {}),
        },
        props.coachingSessionUuid ?? undefined,
    );
}

onMounted(() => {
    void logOfferEvent(AmplitudeEventNames.PERSPECTIVE_OFFER_BANNER_SHOWN);
});

async function handleStart() {
    void logOfferEvent(AmplitudeEventNames.PERSPECTIVE_OFFER_BANNER_CLICKED);
    if (props.offer.path === "team_tools") {
        openUrl(`${page.props.align_host}/perspective/questions`, { target: "_blank" });
        return;
    }
    await handleStartAssessment();
}

async function handleDismiss() {
    dismissPayload.value = { interaction_type: DISMISS_INTERACTION_TYPE };
    await persistDismiss();
    if (dismissError.value) {
        return;
    }
    void logOfferEvent(AmplitudeEventNames.PERSPECTIVE_OFFER_BANNER_DISMISSED);
    emit("dismiss");
}
</script>

<style scoped>
@reference "../../../css/tailwind.css";

/* The card is designed in the tonal palette only, so it pins the semantic and
   button tokens it uses to the tonal ramp instead of following data-theme. */
.perspective-new-chat-offer {
    --color-surface: var(--tonal-core-paper);
    --color-surface-raised: #ffffff;
    --color-ink: var(--tonal-core-ink);
    --color-ink-muted: var(--tonal-neutral-600);
    --color-border: var(--tonal-neutral-200);
    --color-status-error: var(--tonal-red-strong);
    --radius-button: 0.5rem;
    --spacing-button-x: 1rem;
    --spacing-button-y: 0.5rem;
    --text-button: 16px;
    --leading-button: 1.25;
    --font-weight-button: 500;
    --color-button-primary: var(--tonal-neutral-800);
    --color-button-primary-hover: var(--tonal-neutral-900);
    --color-button-primary-disabled: var(--tonal-neutral-200);
    --color-button-tertiary-text: var(--tonal-neutral-900);
    --color-button-tertiary-hover-text: var(--tonal-neutral-600);
    --color-button-tertiary-disabled: transparent;
}

.perspective-new-chat-offer-eyebrow {
    @apply font-scaffold font-medium text-[13px] leading-[18px];
}

/* Discs drop in left to right and settle with two diminishing bounces; each
   overlap fades in on the beat its second disc lands. */
.perspective-mark-dot {
    transform-box: fill-box;
    transform-origin: 50% 100%;
    animation: perspective-mark-drop 0.72s both;
    animation-delay: calc(0.1s + var(--i) * 0.1s);
}

.perspective-mark-overlap {
    animation: perspective-mark-fade 0.14s linear both;
    animation-delay: calc(0.1s + var(--i) * 0.1s);
}

@keyframes perspective-mark-drop {
    0% {
        opacity: 0;
        transform: translateY(-11px) scale(0.92, 0.92);
        animation-timing-function: cubic-bezier(0.55, 0.06, 0.68, 0.19);
    }
    18% {
        opacity: 1;
    }
    45% {
        transform: translateY(0) scale(1.12, 0.86);
        animation-timing-function: cubic-bezier(0.33, 0, 0.67, 1);
    }
    62% {
        transform: translateY(-3.2px) scale(1, 1);
        animation-timing-function: cubic-bezier(0.55, 0.06, 0.68, 0.19);
    }
    78% {
        transform: translateY(0) scale(1.06, 0.94);
        animation-timing-function: cubic-bezier(0.33, 0, 0.67, 1);
    }
    90% {
        transform: translateY(-1.1px) scale(1, 1);
    }
    100% {
        opacity: 1;
        transform: translateY(0) scale(1, 1);
    }
}

@keyframes perspective-mark-fade {
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
}

@media (prefers-reduced-motion: reduce) {
    .perspective-mark-dot,
    .perspective-mark-overlap {
        animation: none;
    }
}
</style>
